import * as React from "react";
import "./DropDownMenu.scss";
import IconLarge from "../../atoms/Icon/IconLarge";
import {Icon} from "../../atoms/Icon/Icon";
import {isDefined} from "../../../../utils/Utils";
import DropDownList from "../DropDownList/DropDownList";
import classNames = require("classnames");

interface IProps<T> {
	/**
	 * List of items.
	 */
	items: DropDownMenuItem<T>[];
	/**
	 * Converts item to item label.
	 * @param {T} item
	 * @return {string}
	 */
	itemToLabel: (item: T) => string | JSX.Element;
	/**
	 * Converts item to item id.
	 * @param {T} item
	 * @return {string}
	 */
	itemToId: (item: T) => string;
	/**
	 * Indicates if item is disabled.
	 * @param {T} item
	 * @return {boolean}
	 */
	itemDisabled?: (item: T) => boolean;
	/**
	 * On select callback.
	 * @param {string} id
	 */
	onSelect?: (item: T) => void;
	/**
	 * Selected item id.
	 */
	selectedItemId?: string;
}

interface IState {
	/**
	 * Selected menu data to populate submenu.
	 */
	submenuItems: DropDownMenuItemSubmenuItem[];
	/**
	 * Opened submenu (parent menu item) identifier.
	 */
	openedSubmenuId: string;
	/**
	 * Menu list element currently hovered identifier.
	 */
	menuElementHoveredId: string;
	/**
	 * Top offset for submenu.
	 */
	topOffset: number;
}

export default class DropDownMenu<T> extends React.PureComponent<IProps<T>, IState> {
	constructor(props: IProps<T>) {
		super(props);
		this.state = {
			menuElementHoveredId: null,
			openedSubmenuId: null,
			submenuItems: null,
			topOffset: 0
		};

		this.hideSubmenu = this.hideSubmenu.bind(this);
	}

	componentWillReceiveProps(){
		this.hideSubmenu();
	}

	render() {
		return (
			<div className={"m-dropdown-menu"}>
				<div className={"m-dropdown-menu_menu_scrollbar_wrapper"}>
					<ul className={"m-dropdown-menu_menu"} id={"id-dd-menu"} onScroll={this.hideSubmenu}>
						{this.props.items.map((item, index) => {
							const isSelected = this.isItemSelected(item.item);
							const liId = `id-dd-menu-item${index}`;
							const itemId = this.props.itemToId(item.item);
							return (
								<li key={index}
									onClick={() => this.onSelect(item.item)}
									className={classNames({
										"is-disabled": this.isItemDisabled(item.item),
										"is-selected": isSelected,
										"submenu-opened": this.state.openedSubmenuId === itemId
									})}
									id={liId}
									onMouseEnter={() => this.canHover() && this.setState({menuElementHoveredId: liId})}>
									{this.props.itemToLabel(item.item)}
									{this.state.menuElementHoveredId === liId &&
									<IconLarge icon={Icon.Overflow}
											   onClick={(evt) => {
												   evt.stopPropagation();
												   this.setState({
													   openedSubmenuId: itemId,
													   submenuItems: item.submenuItems,
													   topOffset: document.getElementById(liId).offsetTop -
													   document.getElementById("id-dd-menu").scrollTop
												   });
											   }}/>
									}
									{isSelected && liId !== this.state.menuElementHoveredId && <IconLarge icon={Icon.CheckMark}/>}
								</li>
							);
						})}
					</ul>
				</div>
				{this.state.submenuItems &&
				<div className={"m-dropdown-menu_submenu"} style={{top: this.state.topOffset}}>
					<DropDownList items={this.state.submenuItems}
								  itemToId={() => this.state.openedSubmenuId}
								  itemToLabel={(i: DropDownMenuItemSubmenuItem) => i.label}
								  onSelect={(i: DropDownMenuItemSubmenuItem) => i.onClick(i)}
								  itemDisabled={(i: DropDownMenuItemSubmenuItem) => i.disabled}/>
				</div>
				}
			</div>
		);
	}

	hideSubmenu() {
		this.setState({
			openedSubmenuId: null,
			submenuItems: null,
			topOffset: 0
		});
	}

	/**
	 * On select callback.
	 * @param {T} item
	 */
	onSelect(item: T) {
		if (isDefined(this.props.onSelect) && !this.isItemDisabled(item)) {
			this.props.onSelect(item);
		}
	}

	/**
	 * Indicates if the device has a real hover. iOS emulates mouseenter on the first tap, and showing the
	 * overflow icon then swallows the click, so touch screens never get the hover state.
	 * @return {boolean}
	 */
	canHover() {
		return !window.matchMedia || window.matchMedia("(hover: hover)").matches;
	}

	/**
	 * Indicates if item is selected.
	 * @param {T} item
	 * @return {boolean}
	 */
	isItemSelected(item: T) {
		return this.props.itemToId(item) === this.props.selectedItemId;
	}

	/**
	 * Indicates if item is disabled.
	 * @param {T} item
	 * @return {boolean}
	 */
	isItemDisabled(item: T) {
		return this.props.itemDisabled && this.props.itemDisabled(item);
	}
}

/**
 * Dropdown menu item..
 */
export interface DropDownMenuItem<T> {
	/**
	 * Dropdown menu item data.
	 */
	item: T;
	/**
	 * Dropdown menu submenu data.
	 */
	submenuItems: DropDownMenuItemSubmenuItem[];
}

/**
 * Dropdown menu submenu item.
 */
export interface DropDownMenuItemSubmenuItem {
	/**
	 * Submenu text.
	 */
	label: string;
	/**
	 * Submenu click action.
	 * @param {DropDownMenuItemSubmenuItem} item
	 */
	onClick: (item: DropDownMenuItemSubmenuItem) => void;
	/**
	 * Indicates if submenu item is disabled.
	 */
	disabled?: boolean;
}
